import React, { useEffect, useState } from 'react';
import { formatDateTime } from '../../lib/utils';
import { apiGet, apiPut, apiPost } from '../../contexts/AuthContext';
import { MessageSquare, Mail, MailOpen, Clock, User, Reply, Send } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import LoadError from '../../components/LoadError';

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const fetchMessages = () => {
    setLoadError(false);
    apiGet('/admin/messages')
      .then(r => setMessages(r.data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMessages(); }, []);

  const markAsRead = async (messageId) => {
    if (markingId) return;
    setMarkingId(messageId);
    try {
      await apiPut(`/admin/messages/${messageId}/read`, {});
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, read: true } : m));
      toast.success('Marked as read');
      window.dispatchEvent(new CustomEvent('xp:refresh-notifications'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as read');
    } finally { setMarkingId(null); }
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
    if (expandedId === id) {
      setReplyingTo(null);
      setReplyText('');
    }
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) { toast.error('Please enter a reply'); return; }
    setReplying(true);
    try {
      const res = await apiPost(`/admin/messages/${messageId}/reply`, { reply: replyText });
      setMessages(prev => prev.map(m => {
        if (m.id === messageId) {
          return { ...m, read: true, replies: [...(m.replies || []), res.data] };
        }
        return m;
      }));
      setReplyText('');
      setReplyingTo(null);
      toast.success('Reply sent');
      window.dispatchEvent(new CustomEvent('xp:refresh-notifications'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Partner Messages</h1>
          <p className="text-ink-2 mt-1">View and manage messages from partners</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-4">
          <p className="text-xs text-ink-3 font-medium uppercase tracking-wider">Total Messages</p>
          <p className="text-2xl font-bold text-ink mt-1">{messages.length}</p>
        </div>
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-4">
          <p className="text-xs text-ink-3 font-medium uppercase tracking-wider">Unread</p>
          <p className="text-2xl font-bold text-ember mt-1">{unreadCount}</p>
        </div>
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-4">
          <p className="text-xs text-ink-3 font-medium uppercase tracking-wider">Read</p>
          <p className="text-2xl font-bold text-success mt-1">{messages.length - unreadCount}</p>
        </div>
      </div>

      {/* Messages List */}
      {loadError ? (
        <LoadError what="partner messages" onRetry={() => { setLoading(true); fetchMessages(); }} />
      ) : messages.length === 0 ? (
        <div className="text-center py-16 bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10">
          <MessageSquare className="w-12 h-12 text-ink-3 mx-auto mb-3" />
          <p className="text-ink-2">No messages from partners yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`bg-surface/70 backdrop-blur-xl rounded-xl border p-5 transition-all hover:shadow-md ${!msg.read ? 'border-brand border-l-4' : 'border-contrast/10'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {msg.read
                    ? <MailOpen className="w-5 h-5 text-ink-3 mt-0.5 shrink-0" />
                    : <Mail className="w-5 h-5 text-brand mt-0.5 shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <div role="button" tabIndex={0} aria-expanded={expandedId === msg.id} className="cursor-pointer rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
                      onClick={() => toggleExpand(msg.id)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(msg.id); } }}>
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className={`font-semibold ${!msg.read ? 'text-ink' : 'text-ink-2'}`}>{msg.subject}</h3>
                      <Badge className={msg.read ? 'bg-brand/15 text-success' : 'bg-ember/15 text-ember'}>
                        {msg.read ? 'Read' : 'New'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-3">
                      <span className="flex items-center gap-1 min-w-0"><User className="w-3 h-3" />{msg.partner_name} ({msg.partner_email})</span>
                      <span className="flex items-center gap-1 whitespace-nowrap"><Clock className="w-3 h-3" />{formatDateTime(msg.created_at)}</span>
                    </div>
                    </div>
                    {expandedId === msg.id && (
                      <div className="mt-3 pt-3 border-t border-contrast/10 space-y-3">
                        <p className="text-ink text-sm whitespace-pre-wrap">{msg.message}</p>

                        {/* Existing Replies */}
                        {msg.replies && msg.replies.length > 0 && (
                          <div className="space-y-2 mt-3">
                            <p className="text-xs font-medium text-ink-2 uppercase tracking-wider">Replies</p>
                            {msg.replies.map(r => (
                              <div key={r.id} className="bg-brand/[0.06] rounded-lg p-3 border border-brand/20">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-semibold text-brand">{r.admin_name}</span>
                                  <span className="text-xs text-ink-3">{formatDateTime(r.created_at)}</span>
                                </div>
                                <p className="text-sm text-ink whitespace-pre-wrap">{r.reply}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Reply Input */}
                        {replyingTo === msg.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={replyText}
                              onChange={e => setReplyText(e.target.value)}
                              placeholder="Type your reply..."
                              aria-label="Reply to partner"
                              rows={3}
                              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            />
                            <div className="flex gap-2 justify-end">
                              <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyText(''); }}>Cancel</Button>
                              <Button size="sm" className="bg-brand hover:bg-brand-soft text-white" disabled={replying} onClick={() => handleReply(msg.id)}>
                                <Send className="w-3 h-3 mr-1" /> {replying ? 'Sending...' : 'Send Reply'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="mt-1" onClick={() => { setReplyingTo(msg.id); setReplyText(''); }}>
                            <Reply className="w-3 h-3 mr-1" /> Reply
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0 ml-3">
                  {!msg.read && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markAsRead(msg.id)}
                      disabled={markingId === msg.id}
                    >
                      Mark Read
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
